import {
  ALERT_CRITICALITIES,
  ALERT_CRITICALITY_LABELS,
  ALERT_JUSTIFICATION_MAX,
  alertSchema,
  deviationInputSchema,
  deviationOptionsSchema,
  localIsoDay,
  type AlertCriticalityName,
  type DeviationSubjectName,
} from '@fluxus/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { errorMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/session';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

const textareaClass =
  'w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid';

/**
 * "Informar desvio" (T-094b, RF-52, PRD §9.5; DEC-045): a execução registra um desvio num indicador ou objetivo
 * do seu escopo, que vira um alerta "desvio informado" para a gestão tratar.
 */
export function ReportDeviationButton({
  organizationId,
  projectId,
}: {
  organizationId: string;
  projectId: string;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <TriangleAlert aria-hidden />
        Informar desvio
      </Button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        busy={busy}
        title="Informar desvio"
        description="O desvio vira um alerta para a gestão tratar, com o seu nome."
      >
        {open ? (
          <DeviationForm
            organizationId={organizationId}
            projectId={projectId}
            onDone={() => setOpen(false)}
            onBusyChange={setBusy}
          />
        ) : null}
      </Dialog>
    </>
  );
}

function DeviationForm({
  organizationId,
  projectId,
  onDone,
  onBusyChange,
}: {
  organizationId: string;
  projectId: string;
  onDone: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const base = `/organizations/${organizationId}/projects/${projectId}/alerts`;
  const options = useQuery({
    queryKey: ['alerts', projectId, 'deviation-options'],
    queryFn: ({ signal }) =>
      client.request(`${base}/deviation-options`, { schema: deviationOptionsSchema, signal }),
  });
  // null = ainda não escolhido: começa no assunto que tem opções (indicador, se houver; senão, objetivo).
  const [chosenSubject, setSubject] = useState<DeviationSubjectName | null>(null);
  const [kpiId, setKpiId] = useState('');
  const [objectiveId, setObjectiveId] = useState('');
  const [businessUnitId, setBusinessUnitId] = useState('');
  const [criticality, setCriticality] = useState<AlertCriticalityName>('MEDIUM');
  const [referenceDate, setReferenceDate] = useState(localIsoDay());
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = {
    subject: useId(),
    kpi: useId(),
    objective: useId(),
    unit: useId(),
    criticality: useId(),
    date: useId(),
    description: useId(),
  };

  if (options.isPending) return <LoadingState rows={3} label="Carregando indicadores e objetivos" />;
  if (options.isError) {
    return (
      <ErrorState
        error={options.error}
        onRetry={() => void options.refetch()}
        retrying={options.isFetching}
      />
    );
  }
  const { kpis, objectives, businessUnits } = options.data;
  const subject: DeviationSubjectName = chosenSubject ?? (kpis.length ? 'KPI' : 'OBJECTIVE');
  if (!kpis.length && !objectives.length) {
    return (
      <p className="text-sm text-muted-foreground">
        Não há indicadores nem objetivos ativos nas suas áreas para informar um desvio.
      </p>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = deviationInputSchema.safeParse({
      kpiId: subject === 'KPI' ? kpiId || null : null,
      objectiveId: subject === 'OBJECTIVE' ? objectiveId || null : null,
      businessUnitId: subject === 'KPI' ? businessUnitId || null : null,
      criticality,
      description,
      referenceDate,
    });
    if (!parsed.success) {
      const fields = zodFieldErrors(parsed.error);
      // "Escolha o indicador ou o objetivo": aparece no campo do assunto escolhido.
      const subjectError = fields.subject;
      return setErrors({
        ...fields,
        ...(subjectError ? { [subject === 'KPI' ? 'kpiId' : 'objectiveId']: subjectError } : {}),
      });
    }
    if (parsed.data.referenceDate > localIsoDay()) {
      return setErrors({ referenceDate: 'Use a data de hoje ou uma data passada.' });
    }
    setErrors({});
    setSaving(true);
    onBusyChange(true);
    try {
      const created = await client.request(`${base}/deviations`, {
        method: 'POST',
        body: parsed.data,
        schema: alertSchema,
      });
      await queryClient.invalidateQueries({ queryKey: ['alerts'] });
      toast.success('Desvio informado', { description: `${created.title} — a gestão foi alertada.` });
      onDone();
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id={ids.subject} label="O desvio é em">
          <NativeSelect
            {...fieldA11y(ids.subject)}
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value as DeviationSubjectName);
              setErrors({});
            }}
          >
            <option value="KPI" disabled={!kpis.length}>
              Um indicador
            </option>
            <option value="OBJECTIVE" disabled={!objectives.length}>
              Um objetivo
            </option>
          </NativeSelect>
        </FormField>
        {subject === 'KPI' ? (
          <FormField id={ids.kpi} label="Indicador" error={errors.kpiId}>
            <NativeSelect
              {...fieldA11y(ids.kpi, errors.kpiId)}
              value={kpiId}
              onChange={(e) => setKpiId(e.target.value)}
            >
              <option value="">Escolha o indicador</option>
              {kpis.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name} ({k.code})
                </option>
              ))}
            </NativeSelect>
          </FormField>
        ) : null}
        {subject === 'KPI' && businessUnits.length ? (
          <FormField
            id={ids.unit}
            label="Unidade"
            hint="Onde o desvio aconteceu (opcional)."
            error={errors.businessUnitId}
          >
            <NativeSelect
              {...fieldA11y(ids.unit, errors.businessUnitId)}
              value={businessUnitId}
              onChange={(e) => setBusinessUnitId(e.target.value)}
            >
              <option value="">O indicador todo</option>
              {businessUnits.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </NativeSelect>
          </FormField>
        ) : null}
        {subject === 'OBJECTIVE' ? (
          <FormField id={ids.objective} label="Objetivo" error={errors.objectiveId}>
            <NativeSelect
              {...fieldA11y(ids.objective, errors.objectiveId)}
              value={objectiveId}
              onChange={(e) => setObjectiveId(e.target.value)}
            >
              <option value="">Escolha o objetivo</option>
              {objectives.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </NativeSelect>
          </FormField>
        ) : null}
        <FormField id={ids.criticality} label="Criticidade" error={errors.criticality}>
          <NativeSelect
            {...fieldA11y(ids.criticality, errors.criticality)}
            value={criticality}
            onChange={(e) => setCriticality(e.target.value as AlertCriticalityName)}
          >
            {ALERT_CRITICALITIES.map((c) => (
              <option key={c} value={c}>
                {ALERT_CRITICALITY_LABELS[c]}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.date} label="Data do desvio" error={errors.referenceDate}>
          <Input
            {...fieldA11y(ids.date, errors.referenceDate)}
            type="date"
            value={referenceDate}
            max={localIsoDay()}
            onChange={(e) => setReferenceDate(e.target.value)}
            className="h-11"
          />
        </FormField>
      </div>
      <FormField id={ids.description} label="O que aconteceu" error={errors.description}>
        <textarea
          {...fieldA11y(ids.description, errors.description)}
          value={description}
          rows={4}
          maxLength={ALERT_JUSTIFICATION_MAX}
          placeholder="Ex.: o fornecedor atrasou a entrega e a produção parou dois dias."
          onChange={(e) => setDescription(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onDone} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          Informar desvio
        </Button>
      </div>
    </form>
  );
}
